import type {
  EMBED_EVENT_TYPES,
  EmbedEvent,
  EmbedKind,
} from "@workspace/contracts/embed"

import { postEvents, type Api } from "./api"
import { read, write } from "./storage"

/**
 * What the runtime reports back (`POST /api/embed/events`, batched). Each
 * event's `id` is a client-minted uuidv7 and IS the server's dedupe key, so
 * a batch that is retried inserts nothing twice; `occurredAt` is the client
 * clock. Batches go out every `FLUSH_MS`, or at `FLUSH_AT` events, or when
 * the page is hidden - `keepalive` so that last one lands.
 */

const FLUSH_MS = 2000
const FLUSH_AT = 20
const BATCH_MAX = 100

export type EventType = (typeof EMBED_EVENT_TYPES)[number]

/**
 * A uuidv7: 48 bits of ms time, then random, version and variant bits set
 * - the shape `events.id` expects, minted here because a page cannot wait
 * on the server for an id it needs to dedupe against.
 */
export function uuidv7(now = Date.now()): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  bytes[0] = (now / 2 ** 40) & 0xff
  bytes[1] = (now / 2 ** 32) & 0xff
  bytes[2] = (now / 2 ** 24) & 0xff
  bytes[3] = (now / 2 ** 16) & 0xff
  bytes[4] = (now / 2 ** 8) & 0xff
  bytes[5] = now & 0xff
  bytes[6] = (bytes[6]! & 0x0f) | 0x70
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

const VISITOR_KEY = "usereq:visitor"

/**
 * The visitor's key: minted once per browser, kept in `localStorage`, sent
 * as `clientKey` and used to bucket a split. Random - it identifies a
 * browser to itself, nothing about the person.
 */
export function visitorKey(): string {
  const stored = read("localStorage", VISITOR_KEY)
  if (stored) return stored
  const minted = uuidv7()
  write("localStorage", VISITOR_KEY, minted)
  return minted
}

export interface Reporter {
  report: (
    type: EventType,
    extra?: {
      widgetId?: string
      sessionId?: string
      payload?: Record<string, unknown>
    }
  ) => void
  flush: () => void
}

/** One reporter per element: it knows which embed the events are about. */
export function createReporter(
  api: Api,
  embed: { kind: EmbedKind; id: string }
): Reporter {
  const queue: EmbedEvent[] = []
  let timer: number | null = null
  const clientKey = visitorKey()

  const flush = () => {
    if (timer !== null) {
      window.clearTimeout(timer)
      timer = null
    }
    while (queue.length > 0) {
      const events = queue.splice(0, BATCH_MAX)
      void postEvents(api, { events })
    }
  }

  const report: Reporter["report"] = (type, extra = {}) => {
    queue.push({
      id: uuidv7(),
      type,
      embedKind: embed.kind,
      embedId: embed.id,
      widgetId: extra.widgetId,
      sessionId: extra.sessionId,
      clientKey,
      url: window.location.href.slice(0, 2000),
      occurredAt: new Date().toISOString(),
      payload: extra.payload,
    })
    if (queue.length >= FLUSH_AT) flush()
    else if (timer === null) timer = window.setTimeout(flush, FLUSH_MS)
  }

  window.addEventListener("pagehide", flush)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush()
  })

  return { report, flush }
}
